import type {ViewConfiguration} from '../../../contexts/views/view-configuration'
import {boardInitialPayloadError, type BoardInitialPayload, isBoardInitialPayload} from './board-payload'
import {isViewConfiguration, viewConfigurationValidator} from './config-payload'

interface PayloadSource {
  /** Names the route in the reported message. */
  routeId: string
  /** The `name` of the reported error, which groups the reports for one surface. */
  errorName: string
}

/**
 * Reports a route payload that does not match its contract without breaking the synchronous
 * flow. An async rethrow is how an error reaches telemetry.
 */
export function reportPayloadMismatch(
  errorName: string,
  message: string,
  validatorError?: {instancePath: string; message: string},
): void {
  setTimeout(() => {
    const validationDetails = validatorError
      ? ` at ${validatorError.instancePath === '' ? 'payload' : validatorError.instancePath}: ${validatorError.message}`
      : ''
    const error = new Error(`${message}${validationDetails}`)
    error.name = errorName
    throw error
  })
}

/**
 * The saved configuration a saved view route payload carries. Undefined when it carries none, or
 * one that does not match the contract, which is reported without the value it held.
 */
export function readEnvelopeConfiguration(
  raw: unknown,
  {routeId, errorName}: PayloadSource,
): ViewConfiguration | undefined {
  if (raw === undefined) return undefined
  if (isViewConfiguration(raw)) return raw

  reportPayloadMismatch(
    errorName,
    `${routeId} configuration failed validation`,
    viewConfigurationValidator.Errors(raw)[0],
  )
  return undefined
}

/** Whether a saved view route payload board matches the contract. A mismatch is reported. */
export function isValidBoardContent(raw: unknown, {routeId, errorName}: PayloadSource): raw is BoardInitialPayload {
  if (isBoardInitialPayload(raw)) return true

  reportPayloadMismatch(errorName, `${routeId} board payload failed validation`, boardInitialPayloadError(raw))
  return false
}
